import { ArrowRight, Calendar, Rocket, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: Calendar,
      title: "Book a Demo",
      subtitle: "Discovery & System Mapping",
      description: "Schedule a free consultation where we analyze your current operations and identify automation opportunities tailored to your business.",
      color: "primary"
    },
    {
      number: "02",
      icon: Rocket,
      title: "Deploy Your Neil AI Agent",
      subtitle: "Integrate with CRM + Voice",
      description: "Our team seamlessly integrates robotics and AI voice systems into your existing workflow with full training and support.",
      color: "accent"
    },
    {
      number: "03",
      icon: TrendingUp,
      title: "Scale",
      subtitle: "Track Leads & Automate Growth",
      description: "Monitor real-time performance metrics, ROI tracking, and scale your automation as your business grows.",
      color: "primary"
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background gradient effect */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(circle at 50% 50%, hsl(var(--primary) / 0.15) 0%, transparent 70%)`
        }}
      />

      <div className="relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-orbitron font-bold text-4xl md:text-5xl mb-4">
            Your AI Automation Pipeline in <span className="text-primary">3 Steps</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From consultation to full automation — we handle everything
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isLast = index === steps.length - 1;
            
            return (
              <div key={step.number} className="relative">
                <div className="glass-effect border-[hsl(var(--line))] rounded-2xl p-8 hover:border-primary/40 transition-all duration-300 h-full">
                  {/* Step number badge */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-xl shadow-[0_0_20px_rgba(0,255,200,0.5)]">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="mb-6 mt-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-orbitron font-bold text-2xl mb-2 text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-sm text-primary font-semibold mb-3">
                    {step.subtitle}
                  </p>
                  <p className="text-muted-foreground">
                    {step.description}
                  </p>
                </div>

                {/* Arrow connector - desktop only */}
                {!isLast && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                    <ArrowRight className="w-8 h-8 text-primary animate-pulse" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            asChild
            size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 font-extrabold rounded-xl px-10 py-7 text-xl shadow-[0_0_30px_rgba(0,255,200,0.5)] hover:shadow-[0_0_40px_rgba(0,255,200,0.7)] transition-all duration-300"
          >
            <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
              📅 Start Your Automation Journey
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
